/**
 * Which saved filter set a page uses. Every standard page shares one
 * (`common`, the same filters bar everywhere), the Influencers pages keep
 * their own (`influencer`), and each custom report keeps its own
 * (`custom_dashboard` + its id). The sample-data tree has one set and saves
 * nothing.
 */
import type { FilterScopeRef, SourceId } from '@/data-sources/contract';
import type { FilterScopeKey } from '@/store/slices/filtersSlice';

export type FilterScope = { key: FilterScopeKey; ref: FilterScopeRef | null };

const INFLUENCER_ROUTES = new Set(['influencers', 'influencer', 'influencer-industry']);

export function filterScopeFor(
  source: SourceId,
  routeId: string | undefined,
  params: Readonly<Record<string, string | undefined>>,
): FilterScope {
  if (source !== 'live') return { key: source, ref: null };
  if (routeId === 'custom-report' && params.reportId) {
    return {
      key: `live:dashboard:${params.reportId}`,
      ref: { scope: 'custom_dashboard', dashboardId: params.reportId },
    };
  }
  if (routeId && INFLUENCER_ROUTES.has(routeId))
    return { key: 'live:influencer', ref: { scope: 'influencer' } };
  return { key: 'live:common', ref: { scope: 'common' } };
}

/** A live scope key back to what is saved under it (`null` for a scope that saves nothing). */
export function refForKey(key: FilterScopeKey): FilterScopeRef | null {
  if (key === 'live:common') return { scope: 'common' };
  if (key === 'live:influencer') return { scope: 'influencer' };
  const dashboard = /^live:dashboard:(.+)$/.exec(key);
  return dashboard ? { scope: 'custom_dashboard', dashboardId: dashboard[1]! } : null;
}
